import { z } from 'zod';
import { ClientKind } from './intake.js';
import { IntakeCaseStatus } from './aml.js';
import { isoInstant as instant } from './values.js';
/**
 * Inviting the person of an intake into the firm's portal (ADR-026): the firm invites, Kanos
 * mails a link to the intake's address, the person signs up in the realm `mandant` and accepts
 * - from then on the account answers for the intake in the portal.
 */
/**
 * How long a link may be used after it was mailed - a product default (decision of 2026-10-27),
 * not a rule of the GwG; the firm invites anew when it ran out.
 */
export const INVITATION_VALID_DAYS = 14;
/**
 * `pending` → `accepted` | `revoked` (the database allows no other transition); `expired` is
 * a pending one whose link ran out - shown, not stored.
 */
export const IntakeInvitationStatus = z
    .enum(['pending', 'accepted', 'revoked', 'expired'])
    .meta({ id: 'IntakeInvitationStatus' });
export const IntakeInvitation = z
    .object({
    id: z.uuid(),
    status: IntakeInvitationStatus,
    /** Where the link went: the intake's address when the firm invited. */
    email: z.string(),
    createdAt: instant,
    createdBy: z.string(),
    /** When the e-mail went out; `null` while it is on its way. */
    mailedAt: instant.nullable(),
    expiresAt: instant,
    acceptedAt: instant.nullable(),
    revokedAt: instant.nullable(),
})
    .meta({ id: 'IntakeInvitation' });
/** Newest first; at most one is pending. */
export const IntakeInvitationList = z.array(IntakeInvitation).meta({ id: 'IntakeInvitationList' });
export const IntakeInvitationParams = z
    .object({ caseId: z.uuid(), invitationId: z.uuid() })
    .meta({ id: 'IntakeInvitationParams' });
/** The secret of a link: 32 random bytes, base64url - only ever in the e-mail and its link. */
export const InvitationToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/** The portal opens a link: the token from the address's fragment, never from its path. */
export const InvitationOpening = z
    .object({ token: InvitationToken })
    .meta({ id: 'InvitationOpening' });
/** What the person sees before signing up: who invites, for which address, until when. */
export const InvitationOpened = z
    .object({ firmName: z.string(), email: z.string(), expiresAt: instant })
    .meta({ id: 'InvitationOpened' });
/** An intake as the person answering for it sees it in the portal. */
export const RespondentIntake = z
    .object({
    id: z.uuid(),
    kind: ClientKind,
    name: z.string(),
    status: IntakeCaseStatus,
    /** When the person last sent the data to the firm; `null`: not yet. */
    submittedAt: instant.nullable(),
})
    .meta({ id: 'RespondentIntake' });
export const RespondentIntakeList = z.array(RespondentIntake).meta({ id: 'RespondentIntakeList' });
export const invitationStatusLabel = {
    pending: 'Eingeladen',
    accepted: 'Angenommen',
    revoked: 'Zurückgezogen',
    expired: 'Abgelaufen',
};
/**
 * The firm's former plan (decision of 2026-10-27, ADR-027): a first reminder, further ones at an
 * interval, then the escalation one interval after the last. Superseded by the rules
 * (`IntakeReminderRules`); read only by intake workflows begun before them.
 */
export const IntakeReminderPlan = z.object({
    firstAfterDays: z.int().min(1).max(14),
    intervalDays: z.int().min(1).max(14),
    count: z.int().min(0).max(5),
});
/** After 3 and 7 days, then the firm after 11. */
export const INTAKE_REMINDER_DEFAULT = {
    firstAfterDays: 3,
    intervalDays: 4,
    count: 2,
};
/** What Kanos did while waiting: a reminder to the person, or the notice to the firm. */
export const IntakeReminderKind = z
    .enum(['reminder', 'escalation'])
    .meta({ id: 'IntakeReminderKind' });
export const IntakeReminder = z
    .object({ kind: IntakeReminderKind, attempt: z.int().min(0), at: instant })
    .meta({ id: 'IntakeReminder' });
/** Oldest first. */
export const IntakeReminderList = z.array(IntakeReminder).meta({ id: 'IntakeReminderList' });
//# sourceMappingURL=intake-invitations.js.map